import { createContext, useContext, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TutorialCtx = createContext(null);
const doneKey = (uid, k) => `sv_tour_${uid || "anon"}_${k}_done`;

// Each tour is an ordered list of steps. `target` is a data-testid to spotlight
// (null = a centered welcome/outro card).
export const TOURS = {
  essentials: [
    { target: null, title: "Welcome to Studivault", body: "Take a 30-second tour of the essentials. You can skip anytime." },
    { target: "logo-link", title: "Home base", body: "Tap the logo anytime to jump back to your dashboard." },
    { target: "nav-subcourses", title: "Your Studies", body: "Every study packs AI notes, summaries, flashcards and a quiz — generated for you." },
    { target: "fab-new", title: "Create anything", body: "Turn a PDF, your own voice, a lecture video or a YouTube link into a full study set." },
    { target: "nav-review", title: "Daily Review", body: "Spaced-repetition flashcards. The red badge shows how many cards are due today." },
    { target: "nav-explore", title: "Explore", body: "Discover and import study packs shared by the community." },
    { target: "gems-chip", title: "Gems", body: "Earn gems by studying, then spend them on power-ups in the shop." },
    { target: "streak-badge", title: "Streaks", body: "Study a little every day to grow your streak. Don't break the chain!" },
    { target: null, title: "You're all set", body: "You'll get a quick tour of each area as you explore. Restart this anytime from Settings → Appearance." },
  ],
  study: [
    { target: null, title: "Inside a study", body: "Nice — your study is ready. Here's a quick tour of everything it gives you." },
    { target: "tab-notes", title: "Notes", body: "Clean, structured notes generated from your material." },
    { target: "tab-flashcards", title: "Cards", body: "Spaced-repetition flashcards that schedule their own reviews." },
    { target: "tab-quiz", title: "Quiz", body: "Auto-generated questions to test your recall and pace." },
    { target: "tab-tutor", title: "AI Tutor", body: "Chat with a tutor about this exact study — quiz me, Socratic, explainer or coach." },
    { target: "tab-exam_mode", title: "Mock Exam", body: "Sit a timed exam — type your answers or upload handwritten ones for OCR grading." },
    { target: "tab-exam", title: "Exam Forecast", body: "The questions most likely to appear on your real exam, with model approaches." },
    { target: "sc-techniques-banner", title: "Study Techniques", body: "Practice Feynman, Blurting and SQ3R — proven active-recall methods." },
    { target: "sc-share-pack-btn", title: "Publish & earn", body: "Share this study to the community marketplace and earn gems when others import it." },
  ],
  explore: [
    { target: null, title: "Explore the community", body: "A marketplace of study packs and courses made by other learners." },
    { target: "explore-search-input", title: "Search anything", body: "Find packs by topic, subject or exam name." },
    { target: "explore-filter-trending", title: "Sort & discover", body: "Switch between Trending, Recent and Most-imported — then open any pack to preview and import it." },
  ],
  courses: [
    { target: null, title: "Courses", body: "Bundle 2–5 of your studies into one course with a merged flashcard deck and a combined exam." },
    { target: "new-course-btn", title: "Build a course", body: "Start here to combine your studies into a comprehensive course." },
  ],
  awards: [
    { target: null, title: "Awards", body: "Unlock achievements as you study — each one grants XP and gems." },
    { target: "gem-balance", title: "Your gems", body: "Earned from achievements, daily logins and referrals. Spend them in the shop." },
    { target: "filter-mythic", title: "Chase the rare ones", body: "Filter by rarity to see what's left to unlock, then share the ones you've earned." },
  ],
};

// Soft, elegant two-note chime synthesized via the Web Audio API (no asset needed).
export function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const notes = [[783.99, 0], [1174.66, 0.09]]; // G5 -> D6, gentle rising fifth
    notes.forEach(([freq, t]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.linearRampToValueAtTime(0.09, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.45);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.5);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  } catch {}
}

export const TutorialProvider = ({ children }) => {
  const [tour, setTour] = useState(null); // active tour key, or null
  const [index, setIndex] = useState(0);
  const running = tour !== null;
  const steps = tour ? TOURS[tour] || [] : [];

  const { user } = useAuth();
  const uid = user?.id;
  const seen = useCallback((k) => {
    if (user?.tours_done?.includes(k)) return true;
    try {
      return localStorage.getItem(doneKey(uid, k)) === "1" || localStorage.getItem(`sv_tour_${k}_done`) === "1";
    } catch { return false; }
  }, [uid, user?.tours_done]);
  const markSeen = useCallback((k) => {
    try { localStorage.setItem(doneKey(uid, k), "1"); } catch {}
    if (uid) api.post(`/auth/tours/${k}/done`).catch(() => {});
  }, [uid]);

  const start = useCallback((k = "essentials") => {
    if (!TOURS[k]) return;
    setIndex(0);
    setTour(k);
    playChime();
  }, []);

  // Start a tour only if it hasn't been seen, essentials is done, and nothing is running.
  const startIfNew = useCallback((k) => {
    if (!TOURS[k] || !uid) return;
    if (k !== "essentials" && !seen("essentials")) return;
    if (seen(k)) return;
    if (tour) return;
    setIndex(0);
    setTour(k);
    playChime();
  }, [tour, seen, uid]);

  const finish = useCallback(() => {
    if (tour) markSeen(tour);
    setTour(null);
  }, [tour, markSeen]);

  // Skip = opt out of every tour, persisted locally and on the account.
  const skip = useCallback(() => {
    Object.keys(TOURS).forEach(markSeen);
    setTour(null);
  }, [markSeen]);

  const next = useCallback(() => {
    setIndex((i) => {
      const s = tour ? TOURS[tour] || [] : [];
      if (i >= s.length - 1) { finish(); return i; }
      playChime();
      return i + 1;
    });
  }, [tour, finish]);

  const prev = useCallback(() => {
    setIndex((i) => { if (i > 0) playChime(); return Math.max(0, i - 1); });
  }, []);

  return (
    <TutorialCtx.Provider value={{ running, tour, index, step: steps[index], total: steps.length, start, startIfNew, stop: finish, skip, next, prev, seen }}>
      {children}
    </TutorialCtx.Provider>
  );
};

export const useTutorial = () => useContext(TutorialCtx);
